// Goal chip + goal card (IMPLEMENTATION §2.6 "Goal chip", R17 v2, M4a #4 / M4c decision 12, UX §2): one line
// `▶ {action} · {price} · {eta}` (15 px / 600; ui/format.ts chipView — the R17 v2 best next step when the
// scripted goal is met or far, the reason only on the card), green + a 1 Hz pulse when affordable, greyed when
// a "Build X" goal is already met with nothing better to show. The row stays reserved during the cold open
// (hidden chip, nothing jumps); in Build mode it becomes the Build banner. The hit area is ≥ 44 tall (it
// grows into the gaps). A tap opens the goal card below it: title, price · ETA, the advice / bottleneck
// reason, the scripted how-to and the Loader checklist.

import { fillCopy } from '../copy.ts';
import { shellCtx } from '../ctx.ts';
import { chipView } from '../format.ts';
import { chipShown } from '../intro.ts';
import { chipHitRect } from '../panels.ts';
import { chipIntroSig, introSig, layoutSig, modeSig, panelSig } from '../state.ts';
import { snapshotSig } from '../store.ts';
import { box } from './box.ts';

export function GoalChip() {
  const l = layoutSig.value;
  const snap = snapshotSig.value;
  const phase = introSig.value.phase;
  const mode = modeSig.value?.mode;
  if (!l) return null;
  const ctx = shellCtx();
  const f = ctx.shell.fmt();
  if (mode === 'build') {
    return (
      <div class="build-banner" style={box(l.chip)} data-testid="build-banner">
        <span>{f.copy.build.banner ?? ''}</span>
      </div>
    );
  }
  const goal = snap?.goal ?? null;
  if (!chipShown(phase) || goal === null) return null;
  const v = chipView(f, goal, chipIntroSig.value);
  const hit = chipHitRect(l, ctx.data().ui.hit.visualMin);
  const open = panelSig.value.k === 'goal';
  const cfg = ctx.data().ui.goalChip;
  return (
    <button
      type="button"
      class={`goal-chip ctl tone-${v.tone} ${open ? 'open' : ''}`}
      style={{ ...box(hit), '--chip-pulse': `${1 / cfg.pulseHz}s` }}
      data-hit="goal-chip"
      data-testid="goal-chip"
      data-fx="goalChip"
      aria-expanded={open}
      onClick={() => {
        ctx.actions.click();
        ctx.shell.togglePanel('goal');
      }}
    >
      <span class="goal-pill" style={{ '--chip-h': `${l.chip.h}px`, fontSize: `${cfg.textPx}px` }}>
        <span class="goal-text" data-testid="goal-text">
          {v.text}
        </span>
      </span>
    </button>
  );
}

export function GoalCard() {
  const l = layoutSig.value;
  const snap = snapshotSig.value;
  if (!l || panelSig.value.k !== 'goal') return null;
  const goal = snap?.goal ?? null;
  if (goal === null) return null;
  const ctx = shellCtx();
  const f = ctx.shell.fmt();
  const v = chipView(f, goal);
  const c = f.copy.goal;
  const gutter = ctx.data().ui.layout.gutter;
  const top = l.chip.y + l.chip.h;
  return (
    <div
      class="goal-card ctl"
      data-testid="goal-card"
      style={{
        left: `${l.column.x + gutter}px`,
        top: `${top}px`,
        width: `${l.column.w - 2 * gutter}px`,
      }}
    >
      <div class="goal-card-head">
        <span class="goal-card-title">{v.title}</span>
        <button
          type="button"
          class="icon-btn ctl"
          data-hit="goal-close"
          aria-label={f.copy.cards.close ?? ''}
          onClick={() => ctx.shell.closePanel()}
        >
          ✕
        </button>
      </div>
      {v.tone === 'met' ? <p class="goal-card-met">{c.met ?? ''}</p> : null}
      {v.priceLine !== null ? <p class="goal-card-price">{v.priceLine}</p> : null}
      {v.reason !== null ? (
        <p class="goal-card-reason" data-testid="goal-reason">
          {v.reason}
        </p>
      ) : null}
      {v.help !== null ? <p class="goal-card-help">{v.help}</p> : null}
      {v.checklist.length > 0 ? (
        <p class="goal-card-check" data-testid="goal-checklist">
          <b>{c.checklist ?? ''}:</b>{' '}
          {v.checklist
            .map((x) =>
              fillCopy((x.made ? c.checkDone : c.checkTodo) ?? '{name}', { name: x.name }),
            )
            .join('  ')}
        </p>
      ) : null}
    </div>
  );
}
